import { formatPauseDate } from '../../shared/utils/subscription-pause.copy';
import { toDate, toYmd, zonedDay } from '../../shared/utils/subscription-pause.util';
import { FilterCategory } from '../../shared/components/filter-modal/filter-modal.component';
import { DateRange } from '../../shared/components/date-range-picker/date-range-picker.component';
import {
  MembershipPauseEvent,
  MembershipPauseEventListParams,
  MembershipPauseEventRow,
  PauseEventAction,
  PauseEventAppliedFilters,
  PauseEventPayload,
  PauseEventVariant,
  PauseSmsFilter,
  PauseSmsState,
} from '../models/membership-pause-event.model';

/**
 * Display rules for the super-admin Membership Pause Events tab (CWS-1648). Pure functions over
 * the API row; the component only renders what `toPauseEventRow` returns.
 */

const EMPTY = '—';

const ACTION_DISPLAY: Record<PauseEventAction, { label: string; variant: PauseEventVariant }> = {
  scheduled: { label: 'Pause scheduled', variant: 'neutral' },
  paused: { label: 'Paused', variant: 'info' },
  scheduled_canceled: { label: 'Scheduled pause cancelled', variant: 'neutral' },
  early_resume: { label: 'Resumed early', variant: 'positive' },
  auto_resume: { label: 'Auto resumed', variant: 'positive' },
  billing_restarted: { label: 'Billing restarted', variant: 'info' },
  membership_canceled: { label: 'Membership cancelled', variant: 'danger' },
};

const SMS_DISPLAY: Record<PauseSmsFilter, { label: string; variant: PauseEventVariant }> = {
  pending: { label: 'Pending', variant: 'warning' },
  attempting: { label: 'Sending', variant: 'warning' },
  sent: { label: 'Sent', variant: 'positive' },
  failed: { label: 'Failed', variant: 'danger' },
  skipped: { label: 'Skipped', variant: 'neutral' },
  not_queued: { label: 'Not queued', variant: 'warning' },
};

export const PAUSE_EVENT_ACTION_OPTIONS = (Object.keys(ACTION_DISPLAY) as PauseEventAction[]).map(
  value => ({ value, label: ACTION_DISPLAY[value].label })
);

export const PAUSE_SMS_FILTER_OPTIONS = (Object.keys(SMS_DISPLAY) as PauseSmsFilter[]).map(
  value => ({ value, label: SMS_DISPLAY[value].label })
);

export function pauseEventActionLabel(action: string): string {
  return ACTION_DISPLAY[action as PauseEventAction]?.label ?? action;
}

export function pauseEventActionVariant(action: string): PauseEventVariant {
  return ACTION_DISPLAY[action as PauseEventAction]?.variant ?? 'neutral';
}

/** Filter modal category ids; `createdDate` must match the modal's custom range config. */
export const PAUSE_FILTER_CATEGORY = {
  action: 'action',
  smsStatus: 'smsStatus',
  date: 'createdDate',
} as const;

function radioOptions(
  options: { value: string; label: string }[],
  selectedValue: string
): FilterCategory['options'] {
  return options.map(option => ({
    id: option.value,
    label: option.label,
    value: option.value,
    selected: option.value === selectedValue,
  }));
}

/** Filter modal content for the applied filters. Action and SMS status are single choice. */
export function buildPauseEventFilterCategories(
  applied: PauseEventAppliedFilters
): FilterCategory[] {
  return [
    {
      id: PAUSE_FILTER_CATEGORY.action,
      name: 'Action',
      type: 'radio',
      options: radioOptions(PAUSE_EVENT_ACTION_OPTIONS, applied.action),
    },
    {
      id: PAUSE_FILTER_CATEGORY.smsStatus,
      name: 'SMS Status',
      type: 'radio',
      options: radioOptions(PAUSE_SMS_FILTER_OPTIONS, applied.smsStatus),
    },
    { id: PAUSE_FILTER_CATEGORY.date, name: 'Custom Date', type: 'date-range', options: [] },
  ];
}

/** Applied filters from the modal. A half-open date range is dropped rather than half-applied. */
export function resolvePauseEventFiltersFromCategories(
  categories: FilterCategory[],
  dateRange: DateRange
): PauseEventAppliedFilters {
  const selected = (id: string): string =>
    categories.find(category => category.id === id)?.options.find(option => option.selected)
      ?.value ?? '';
  const hasRange = !!dateRange.startDate && !!dateRange.endDate;
  return {
    action: selected(PAUSE_FILTER_CATEGORY.action) as PauseEventAppliedFilters['action'],
    smsStatus: selected(PAUSE_FILTER_CATEGORY.smsStatus) as PauseEventAppliedFilters['smsStatus'],
    dateFrom: hasRange ? dateRange.startDate : null,
    dateTo: hasRange ? dateRange.endDate : null,
  };
}

/** Badge on the Filter button: action, SMS status and the date range count once each. */
export function countActiveFilters(applied: PauseEventAppliedFilters): number {
  return (
    (applied.action ? 1 : 0) +
    (applied.smsStatus ? 1 : 0) +
    (applied.dateFrom && applied.dateTo ? 1 : 0)
  );
}

/** Query params for the list endpoint; empty filters are omitted. */
export function toPauseEventQuery(
  applied: PauseEventAppliedFilters,
  search: string
): MembershipPauseEventListParams {
  return {
    action: applied.action || undefined,
    sms_status: applied.smsStatus || undefined,
    date_from: applied.dateFrom ? toYmd(applied.dateFrom) : undefined,
    date_to: applied.dateTo ? toYmd(applied.dateTo) : undefined,
    search: search.trim() || undefined,
  };
}

/** "Oct 7, 2026, 3:45 PM MST" at the shop's zone (browser zone without a usable one). */
function formatInstant(value: string | null | undefined, timeZone?: string | null): string {
  const date = toDate(value);
  if (!date) {
    return EMPTY;
  }
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  };
  try {
    return date.toLocaleString('en-US', { ...options, timeZone: timeZone || undefined });
  } catch {
    // An unknown zone name: fall back to the browser's zone.
    return date.toLocaleString('en-US', options);
  }
}

function formatShopDay(value: string | null | undefined, timeZone?: string | null): string | null {
  const date = toDate(value);
  return date ? formatPauseDate(zonedDay(date, timeZone)) : null;
}

/**
 * SMS cell. Only paused / early_resume / auto_resume send an SMS (the backend's `sms_eligible`),
 * so a missing notification row is "not queued" for those and not applicable for the rest.
 */
export function pauseSmsState(
  event: MembershipPauseEvent,
  timeZone?: string | null
): PauseSmsState {
  if (!event.sms_status) {
    if (!event.sms_eligible) {
      return { kind: 'na', label: EMPTY, variant: null, detail: '' };
    }
    return {
      kind: 'not_queued',
      ...SMS_DISPLAY.not_queued,
      detail: 'No SMS was queued for this event',
    };
  }
  const display = SMS_DISPLAY[event.sms_status] ?? { label: event.sms_status, variant: 'neutral' };
  const attempts = event.sms_attempt_count ?? 0;
  const attemptsText = `${attempts} attempt${attempts === 1 ? '' : 's'}`;
  let detail = attemptsText;
  if (event.sms_status === 'sent' && event.sms_sent_at) {
    detail = `${formatInstant(event.sms_sent_at, timeZone)} · ${attemptsText}`;
  } else if (event.sms_failure_reason) {
    detail = `${event.sms_failure_reason} · ${attemptsText}`;
  }
  return { kind: event.sms_status, ...display, detail };
}

/**
 * Pause window lines, one fact each: "Nov 1, 2026 → Dec 1, 2026", "12 days banked",
 * "Next charge on Nov 20, 2026". Only the facts the action logged appear.
 */
export function pauseWindowLines(payload: PauseEventPayload, timeZone?: string | null): string[] {
  const lines: string[] = [];
  const start = formatShopDay(payload?.start, timeZone);
  const resume = formatShopDay(payload?.resume_date ?? payload?.original_resume_date, timeZone);
  if (start && resume) {
    lines.push(`${start} → ${resume}`);
  } else if (start || resume) {
    lines.push(start ? `From ${start}` : `Until ${resume}`);
  }
  const banked = payload?.banked_days;
  if (typeof banked === 'number' && banked > 0) {
    lines.push(`${banked} day${banked === 1 ? '' : 's'} banked`);
  }
  const restart = formatShopDay(payload?.billing_restart_at ?? payload?.next_charge_date, timeZone);
  if (restart) {
    lines.push(`Next charge on ${restart}`);
  }
  return lines.length ? lines : [EMPTY];
}

/** Actions the platform records itself, with no staff actor (sweeps, billing, cancel webhooks). */
const SYSTEM_ACTIONS: PauseEventAction[] = [
  'auto_resume',
  'billing_restarted',
  'membership_canceled',
];

/** System-driven by design: a system action, a job-started pause, or the start-time cancel. */
function isSystemEvent(event: MembershipPauseEvent): boolean {
  return (
    SYSTEM_ACTIONS.includes(event.action) ||
    event.payload?.source === 'job' ||
    !!event.payload?.reason
  );
}

/**
 * Staff-driven actions always record the user, and `performed_by` is nulled when that user is
 * deleted. So a missing actor on a system event is "System"; on any other event the user is gone.
 */
export function performedByLabel(event: MembershipPauseEvent): string {
  if (!event.performed_by) {
    return isSystemEvent(event) ? 'System' : 'Removed User';
  }
  return event.performed_by_name || event.performed_by_email || EMPTY;
}

/** Every date on the row is shown in the zone of the membership's location (`event.time_zone`). */
export function toPauseEventRow(event: MembershipPauseEvent): MembershipPauseEventRow {
  const timeZone = event.time_zone;
  return {
    id: event.id,
    subscriptionId: event.subscription_id,
    timestamp: formatInstant(event.created_at, timeZone),
    timeZone,
    customerName: event.customer_name || '',
    licensePlate: event.license_plate || '',
    planName: event.package_name || EMPTY,
    actionLabel: pauseEventActionLabel(event.action),
    actionVariant: pauseEventActionVariant(event.action),
    pauseWindowLines: pauseWindowLines(event.payload, timeZone),
    performedBy: performedByLabel(event),
    sms: pauseSmsState(event, timeZone),
    providerMessageId: event.sms_provider_message_id || EMPTY,
    payload: event.payload ?? {},
  };
}
